import axios from 'axios';
import api, { get, post } from './https';

export type AdPlatform = 'META' | 'GOOGLE' | 'ORGANIC' | 'OTHER';
export type CampaignStatus = 'ACTIVE' | 'PAUSED' | 'ENDED' | 'NO_CAMPAIGNS';
export type MarketingLeadStatus = 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'LOST';

// What the ad platform's lead form captured, before an admin fills the rest
// in at conversion time. Every field is optional server-side.
export interface MarketingLeadAddress {
  line1: string;
  line2: string;
  town: string;
  county: string;
  postcode: string;
}

// What ops has confirmed about "what job is this" while reviewing the lead
// (Convert drawer's "Save" action), saved independently of actually
// converting. Meta never sends this, so there's no captured value to fall
// back to — it stays undefined until an admin sets it.
export interface MarketingLeadConversionDraft {
  subType?: string;
  serviceId?: string;
  quantity?: number;
  propertyType?: 'RESIDENTIAL' | 'COMMERCIAL';
  applianceKey?: string;
  applianceLabel?: string;
  componentKey?: string;
  componentLabel?: string;
  customAttributes?: ConvertLeadCustomAttributes;
  updatedAt?: string;
  updatedBy?: string;
}

export interface MarketingLeadConversion {
  enquiry: string;
  enquiryNumber: string;
  // The Booking created alongside the Enquiry. /enquires lists bookings, so
  // this is what "Open enquiry" links to. Null for leads converted before
  // conversion started creating one.
  booking: string | null;
  bookingNumber: string;
  user: string | null;
  convertedAt: string | null;
}

export interface MarketingLead {
  // Provenance (Meta): which ad account / Page / form the lead came from.
  adAccountId?: string;
  pageId?: string;
  formId?: string;
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  platform: AdPlatform;
  campaignId: string | null;
  campaignName: string;
  status: MarketingLeadStatus;
  receivedOn: string;
  roas: number | null;
  address?: MarketingLeadAddress;
  conversion?: MarketingLeadConversion;
  conversionDraft?: MarketingLeadConversionDraft;
  createdAt: string;
  updatedAt: string;
}

export interface Campaign {
  _id: string;
  name: string;
  platform: AdPlatform;
  status: 'ACTIVE' | 'PAUSED' | 'ENDED';
}

export interface PlatformMetrics {
  platform: AdPlatform;
  campaignStatus: CampaignStatus;
  campaignName: string | null;
  leads: number;
  qualifiedLeads: number;
  // Leads with status CONVERTED (subset of qualifiedLeads).
  convertedLeads?: number;
  callsReceived: number;
  // Meta/Google's own attributed lead count from the insights sync — for
  // reconciliation against `leads` (what the webhook actually delivered).
  reportedLeads?: number;
  // Campaign-days of spend the insights sync stored for this range. 0 means
  // amountSpent/costPerLead below are absent data, not measured zeros — show
  // "—", not a confident zero.
  spendDays?: number;
  costPerLead: number;
  amountSpent: number;
  // null = the previous period had nothing to compare against.
  changePct: {
    leads: number | null;
    qualifiedLeads: number | null;
    callsReceived: number | null;
    costPerLead: number | null;
    amountSpent: number | null;
  };
}

/**
 * The extent of the stored data, unbounded by the selected range.
 *
 * A history import pulls 90 days of leads and a year of spend, while these
 * screens default to the trailing 30 days — so without this the page can show
 * "0 leads" over a database holding months of them, which is exactly how the
 * Meta screen and the Leads overview ended up disagreeing.
 */
export interface LeadDataCoverage {
  leads: { total: number; firstAt: string | null; lastAt: string | null };
  spend: { days: number; amountSpent: number; firstAt: string | null; lastAt: string | null };
}

export interface LeadSummaryResponse {
  range: { from: string; to: string; days?: number };
  previousRange?: { from: string; to: string };
  // ISO 4217 of the connected ad account — every money figure here is in it.
  currency?: string;
  coverage?: {
    overall: LeadDataCoverage;
    META: LeadDataCoverage;
    GOOGLE: LeadDataCoverage;
  };
  overall: Omit<PlatformMetrics, 'platform' | 'campaignStatus' | 'campaignName'>;
  platforms: {
    META: PlatformMetrics;
    GOOGLE: PlatformMetrics;
  };
}

export interface MarketingLeadListParams {
  page?: number;
  limit?: number;
  search?: string;
  source?: AdPlatform;
  campaignId?: string;
  status?: MarketingLeadStatus;
  // Meta only: narrow to one connected Page.
  pageId?: string;
  // YYYY-MM-DD, inclusive.
  from?: string;
  to?: string;
}

export interface MarketingLeadListResponse {
  data: MarketingLead[];
  total: number;
  page: number;
  limit: number;
}

const BASE = '/api/v1/admin/leads';

const queryString = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export const getLeadSummary = async (params: {
  from?: string;
  to?: string;
}): Promise<LeadSummaryResponse> => {
  const response = await get(`${BASE}/summary${queryString(params)}`);
  return response.data.data;
};

export const getMarketingLeads = async (
  params: MarketingLeadListParams,
): Promise<MarketingLeadListResponse> => {
  const page = params.page || 1;
  const limit = params.limit || 10;
  const response = await get(
    `${BASE}${queryString({
      page,
      limit,
      search: params.search,
      source: params.source,
      campaignId: params.campaignId,
      status: params.status,
      pageId: params.pageId,
      from: params.from,
      to: params.to,
    })}`,
  );
  return {
    data: response.data.data || [],
    total: response.data.count || 0,
    page,
    limit,
  };
};

export const getMarketingLeadCampaigns = async (): Promise<Campaign[]> => {
  const response = await get(`${BASE}/campaigns`);
  return response.data.data || [];
};

// ── Ad-platform integrations (connect/disconnect/sync) ──────────────────────

export type IntegrationStatus = 'DISCONNECTED' | 'CONNECTED' | 'ERROR';
export type IntegrationErrorCode = 'TOKEN_INVALID' | 'PERMISSION' | 'RATE_LIMIT' | 'CONFIG' | 'TRANSIENT';
export type MetaTokenType = 'USER' | 'SYSTEM_USER';

export interface IntegrationAccountStatus {
  platform: 'META' | 'GOOGLE';
  status: IntegrationStatus;
  externalAccountId: string | null;
  externalAccountName?: string | null;
  externalAccountCurrency?: string | null;
  connectedAt: string | null;
  lastSyncedAt: string | null;
  lastSyncAttemptAt?: string | null;
  lastSyncError: string | null;
  // Which kind of failure lastSyncError is. Only TOKEN_INVALID means
  // "reconnect"; CONFIG means the server needs .env fixed + a restart.
  lastErrorCode?: IntegrationErrorCode | null;
  // Meta: an ad account AND a Page have been chosen. A CONNECTED account
  // with setupComplete=false has a token but can't sync or receive leads.
  setupComplete?: boolean;
  reconnectRequired?: boolean;
  // Meta only
  tokenType?: MetaTokenType | null;
  tokenExpiresAt?: string | null;
  tokenExpiresInDays?: number | null;
  // Every Page chosen for lead forms, each subscribed (or not) to leadgen.
  pages?: { id: string; name: string; subscribedAt: string | null; subscribeError: string | null }[];
  allPagesSubscribed?: boolean;
  // Config health of the RUNNING server process (see getStatus on the backend)
  credentialsConfigured?: boolean;
  encryptionConfigured?: boolean;
}

export type IntegrationStatusResponse = Record<'META' | 'GOOGLE', IntegrationAccountStatus>;

// ── Meta setup (ad account + Page selection, System User token, health) ─────

export interface MetaAdAccountOption {
  id: string; // "act_…"
  accountId: string;
  name: string;
  currency: string;
  isActive: boolean;
  accountStatus: number;
  businessName: string;
  source?: string;
}

export interface MetaPageOption {
  id: string;
  name: string;
  canAdvertise: boolean;
  tasks: string[];
  // 'user' = the person has a direct role; 'business:<name>' = reached via a
  // Business they belong to. hasToken=false means Meta issued no Page token
  // (no task on the Page) — it can be listed but not subscribed.
  source?: string;
  hasToken?: boolean;
}

export interface MetaOptionsResponse {
  adAccounts: MetaAdAccountOption[];
  pages: MetaPageOption[];
  businesses?: { id: string; name: string }[];
  // Set when Meta returned no ad accounts or no Pages — explains why.
  hint?: string | null;
  selected: { adAccountId: string | null; pageIds: string[] };
}

// ── Analytics ────────────────────────────────────────────────────────────────

export interface LeadTimeseriesPoint {
  date: string; // YYYY-MM-DD (UTC)
  leads: number;
  qualified: number;
  converted: number;
  reportedLeads: number;
  amountSpent: number;
  costPerLead: number;
}

export interface CampaignPerformanceRow {
  campaignId: string | null;
  campaignName: string;
  platform: AdPlatform;
  status: 'ACTIVE' | 'PAUSED' | 'ENDED' | 'UNKNOWN';
  leads: number;
  qualified: number;
  converted: number;
  reportedLeads: number;
  amountSpent: number;
  costPerLead: number | null;
}

export const getLeadTimeseries = async (params: {
  from: string;
  to: string;
  platform?: AdPlatform;
}): Promise<LeadTimeseriesPoint[]> => {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  if (params.platform) query.set('platform', params.platform);
  const response = await get(`${BASE}/timeseries?${query.toString()}`);
  return response.data.data?.points || [];
};

export const getCampaignPerformance = async (params: {
  from: string;
  to: string;
  platform?: AdPlatform;
}): Promise<CampaignPerformanceRow[]> => {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  if (params.platform) query.set('platform', params.platform);
  const response = await get(`${BASE}/campaigns/performance?${query.toString()}`);
  return response.data.data?.campaigns || [];
};

export interface MetaHealthCheck {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
  fix: string;
}

export interface MetaHealthResponse {
  ok: boolean;
  checkedAt: string;
  checks: MetaHealthCheck[];
  account: IntegrationAccountStatus;
  urls: { oauthRedirect: string | null; webhook: string | null };
}

const unwrapMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

export const getMetaOptions = async (): Promise<MetaOptionsResponse> => {
  try {
    const response = await get(`${BASE}/integrations/META/options`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(unwrapMessage(error, 'Could not load Meta ad accounts and Pages.'));
  }
};

export const selectMetaTargets = async (payload: {
  adAccountId: string;
  pageIds: string[];
}): Promise<{ message: string; account: IntegrationAccountStatus; subscribeError: { code: string; message: string } | null }> => {
  try {
    const response = await post(`${BASE}/integrations/META/targets`, payload);
    return { message: response.data.message, ...response.data.data };
  } catch (error: any) {
    throw new Error(unwrapMessage(error, 'Could not save the Meta ad account and Page.'));
  }
};

export const connectMetaWithToken = async (
  accessToken: string,
): Promise<{
  message: string;
  account: IntegrationAccountStatus;
  adAccounts: MetaAdAccountOption[];
  pages: MetaPageOption[];
}> => {
  try {
    const response = await post(`${BASE}/integrations/META/connect-token`, { accessToken });
    return { message: response.data.message, ...response.data.data };
  } catch (error: any) {
    throw new Error(unwrapMessage(error, 'Meta rejected that token.'));
  }
};

export interface MetaImportSummary {
  days: number;
  adAccountId: string | null;
  adAccountName: string | null;
  campaigns: number;
  statDays: number;
  spend: number;
  reportedLeads: number;
  forms: number;
  leadsSeen: number;
  leadsCreated: number;
  leadsExisting: number;
  pages: { id: string; name: string; forms: number; leadsSeen: number; leadsCreated: number; error: string | null }[];
  warnings: string[];
}

// Backfill campaigns, daily spend and the leads Meta still holds (90 days)
// for every subscribed Page. Safe to re-run — leads are keyed on Meta's id.
export const importMetaHistory = async (
  days = 365,
): Promise<{ message: string; summary: MetaImportSummary }> => {
  try {
    const response = await post(`${BASE}/integrations/META/import`, { days });
    return { message: response.data.message, summary: response.data.data };
  } catch (error: any) {
    throw new Error(unwrapMessage(error, 'Import failed.'));
  }
};

export const getMetaHealth = async (): Promise<MetaHealthResponse> => {
  const response = await get(`${BASE}/integrations/META/health`);
  return response.data.data;
};

export const getIntegrationStatus = async (): Promise<IntegrationStatusResponse> => {
  const response = await get(`${BASE}/integrations`);
  return response.data.data;
};

export const connectIntegration = async (
  platform: 'META' | 'GOOGLE',
): Promise<{ url: string }> => {
  const response = await get(`${BASE}/integrations/${platform}/connect`);
  return response.data.data;
};

export const disconnectIntegration = async (platform: 'META' | 'GOOGLE'): Promise<void> => {
  await post(`${BASE}/integrations/${platform}/disconnect`);
};

export const syncIntegrationNow = async (platform: 'META' | 'GOOGLE'): Promise<void> => {
  try {
    await post(`${BASE}/integrations/${platform}/sync-now`);
  } catch (error: any) {
    // The backend now returns the classified reason (token expired, server
    // misconfigured, rate-limited…) rather than a generic server error.
    throw new Error(unwrapMessage(error, `${platform} sync failed.`));
  }
};

// ── Lead → Enquiry conversion ────────────────────────────────────────────
// See nouvakserverapp/docs (Marketing Lead → Enquiry Conversion) for the
// full contract. This is the lead-detail sidebar's Convert flow.

export type ConversionWarningCode =
  | 'PHONE_UNUSABLE'
  | 'EXISTING_CUSTOMER'
  | 'OPEN_ENQUIRY_EXISTS'
  | 'DUPLICATE_LEAD';

export interface ConversionWarning {
  code: ConversionWarningCode;
  message: string;
}

export interface ConversionExistingUser {
  id: string;
  name: string;
  phoneNumber: string;
  createdAt?: string;
  // Their saved default address, if any — offered as an alternative to
  // whatever the lead itself captured.
  address?: ConvertLeadAddress | null;
}

export interface ConversionOpenEnquiry {
  id: string;
  enquiryNumber: string;
  subType: string;
  status: string;
  createdAt?: string;
}

export interface ConversionOtherLead {
  id: string;
  platform: AdPlatform;
  campaignName: string;
  receivedOn: string;
  enquiryNumber: string;
}

export interface LeadConversionPreview {
  leadId: string;
  alreadyConverted: boolean;
  conversion: MarketingLeadConversion | null;
  convertible: boolean;
  status: MarketingLeadStatus;
  phone: { raw: string; e164: string | null; usable: boolean };
  existingUser: ConversionExistingUser | null;
  openEnquiries: ConversionOpenEnquiry[];
  otherConvertedLeads: ConversionOtherLead[];
  warnings: ConversionWarning[];
}

export const getLeadConversionPreview = async (
  leadId: string,
): Promise<LeadConversionPreview> => {
  const response = await get(`${BASE}/${leadId}/conversion-preview`);
  return response.data.data;
};

export interface ConvertLeadAddress {
  line1?: string;
  line2?: string;
  town?: string;
  county?: string;
  postcode?: string;
}

// Step-4 intake-form answers, collected when the picked component triggers
// the form (componentKey 'other') or the job type isn't in the catalog.
export interface ConvertLeadCustomAttributes {
  applianceOrPartType?: string;
  problemDescription?: string;
}

export interface ConvertLeadPayload {
  subType: string;
  serviceId?: string;
  quantity?: number;
  acType?: string;
  propertyType?: 'RESIDENTIAL' | 'COMMERCIAL';
  // Catalog picks (SbApplianceType/SbComponent) — see utils/dispatchableServices.ts.
  applianceKey?: string;
  applianceLabel?: string;
  componentKey?: string;
  componentLabel?: string;
  customAttributes?: ConvertLeadCustomAttributes;
  customerName?: string;
  email?: string;
  phoneNumberOverride?: string;
  // Set when the admin manually attaches this lead to a specific existing
  // customer (search override) instead of the automatic phone-number match.
  existingUserId?: string;
  address?: ConvertLeadAddress;
  slot?: 'FIRST_HALF' | 'SECOND_HALF';
  date?: string;
  force?: boolean;
}

export interface ConvertLeadResult {
  lead: { id: string; status: MarketingLeadStatus };
  enquiry: {
    id: string;
    enquiryNumber: string;
    subType: string;
    status: string;
    service: { id: string; name: string } | null;
    applianceLabel?: string | null;
    componentLabel?: string | null;
  };
  // The dispatchable job ops works from at /enquires.
  booking: {
    id: string;
    bookingNumber: string;
    status: string;
    slot: string;
    date: string;
  };
  customer: {
    id: string;
    name: string;
    phoneNumber: string;
    reusedExisting: boolean;
  };
  addressSaved: boolean;
}

// Carries the structured 4xx payload the backend attaches to conversion
// errors (see marketingLead.controller.js#failFromError) — the plain-Error
// helpers in ./https discard this, so convertLead below talks to `api`
// directly and reconstructs it.
export interface ConversionErrorData {
  requiresConfirmation?: boolean;
  warnings?: ConversionWarning[];
  existingUser?: ConversionExistingUser | null;
  openEnquiries?: ConversionOpenEnquiry[];
  otherConvertedLeads?: ConversionOtherLead[];
  conversion?: MarketingLeadConversion;
}

export class LeadConversionError extends Error {
  statusCode: number;
  data?: ConversionErrorData;

  constructor(statusCode: number, message: string, data?: ConversionErrorData) {
    super(message);
    this.name = 'LeadConversionError';
    this.statusCode = statusCode;
    this.data = data;
  }
}

export const convertLead = async (
  leadId: string,
  payload: ConvertLeadPayload,
): Promise<ConvertLeadResult> => {
  try {
    const response = await api.post(`${BASE}/${leadId}/convert`, payload);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const body = error.response.data as
        | { message?: string; data?: ConversionErrorData }
        | undefined;
      throw new LeadConversionError(
        error.response.status,
        body?.message || 'Failed to convert this lead',
        body?.data,
      );
    }
    throw new LeadConversionError(0, 'No response from the server. Please try again.');
  }
};

// The Convert drawer's "Save" action — persists an address correction and/or
// the category pick onto the lead without converting, so the work survives
// closing the drawer. Same error shape as convertLead.
export interface UpdateLeadPayload {
  address?: ConvertLeadAddress;
  conversionDraft?: {
    subType?: string;
    serviceId?: string;
    quantity?: number;
    propertyType?: 'RESIDENTIAL' | 'COMMERCIAL' | '';
    applianceKey?: string;
    applianceLabel?: string;
    componentKey?: string;
    componentLabel?: string;
    customAttributes?: ConvertLeadCustomAttributes;
  };
}

export const updateLead = async (
  leadId: string,
  payload: UpdateLeadPayload,
): Promise<MarketingLead> => {
  try {
    // Talks to `api` directly, not the `patch` helper in ./https — that
    // helper discards the response status on error, and callers here need
    // it (see the ConversionErrorData comment above convertLead).
    const response = await api.patch(`${BASE}/${leadId}`, payload);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const body = error.response.data as { message?: string } | undefined;
      throw new LeadConversionError(error.response.status, body?.message || 'Failed to save changes');
    }
    throw new LeadConversionError(0, 'No response from the server. Please try again.');
  }
};
